import type { AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios'
import axios from 'axios'
import reduce from 'lodash-es/reduce'

type EntitiesMap = Partial<Record<string, string>>

import {
  ManagedAccessToken,
  setCookieProvidedScopePlatformAgnostic,
} from '@b-stock/fe-core/bstock-react'

import appConfig, { getHost } from '@config/config'
import CookieManager from '@helpers/CookieManager'

let clientSideManagedAccessToken: ManagedAccessToken | null = null

export const setClientSideManagedAccessToken = (
  managedAccessToken: ManagedAccessToken | null
) => {
  clientSideManagedAccessToken = managedAccessToken
}

const getAccessToken = async (): Promise<string | undefined> => {
  let accessToken: string | null

  if (clientSideManagedAccessToken) {
    accessToken =
      (await clientSideManagedAccessToken.currentWaitForRefresh())?.encoded ??
      null
  } else {
    // We're in SSR, or our CSR hasn't yet initialized the ManagedAccessToken
    // Ideally, we'd be using the appropriate SSR ManagedAccessToken instead
    // of creating a new one... but AP isn't architected to nicely support
    // that, and it's not worth the pain of re-writing the entire XHR layer
    // to do it properly, since we want to get rid of AP eventually anyway.
    // https://bstock.atlassian.net/wiki/spaces/EN/pages/3974627345/2026-02+Accounts+Portal+migration+plan+proposal
    const managedAccessToken = new ManagedAccessToken({
      getCookie: (key) => CookieManager.getCookie(key) ?? null,
      isGetCookieStale: false,
      setCookie: setCookieProvidedScopePlatformAgnostic,
      getHost,
      faClientId: appConfig.api.auth.clientId ?? 'MISSING_FA_CLIENT_ID',
    })
    try {
      accessToken =
        (await managedAccessToken.currentWaitForRefresh())?.encoded ?? null
    } finally {
      managedAccessToken.dispose()
    }
  }
  return accessToken ?? undefined
}

function applyEntities(url: string, data?: EntitiesMap) {
  if (!data) {
    return url
  }

  return reduce(
    data,
    (str, value, key) => (value ? str.replace(`:${key}`, value) : str),
    url
  )
}

type CommonApiCallOptions = Omit<
  AxiosRequestConfig,
  'method' | 'data' | 'params' | 'headers'
> & {
  // Axios types these as `any`; `unknown` keeps them opaque without propagating `any`.
  params?: unknown
  entities?: EntitiesMap
  headers?: Partial<RawAxiosRequestHeaders>
}

async function apiCall<T>(
  url = '',
  data: unknown = null,
  {
    method = 'GET',
    params,
    headers: additionalHeaders,
    responseType = 'json',
    entities,
    ...additionalOptions
  }: CommonApiCallOptions & {
    method?: AxiosRequestConfig['method']
  } = {}
) {
  const accessToken = await getAccessToken()
  const preparedUrl = applyEntities(url, entities)

  const headers = {
    'Content-Type': 'application/json',
    // comment out line 32-42 as workaround when running BE locally
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...additionalHeaders,
  }

  // Deliberately not annotated `AxiosRequestConfig` — this object also carries
  // fetch-only options (mode/cache/credentials/redirect/referrerPolicy) that
  // Axios ignores and that type does not declare.
  const config = {
    method, // *GET, POST, PUT, DELETE, etc.
    // mode: 'no-cors', // set as no-cors as workaround when running BE locally
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers,
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    responseType, // json, arraybuffer, document, text, stream
    params,
    ...(data ? { data } : {}), // body data type must match 'Content-Type' header
    ...additionalOptions,
  }

  return axios<T>(preparedUrl, config)
}

export function get<T>(
  url: string,
  params?: unknown,
  options?: Omit<CommonApiCallOptions, 'params'>
) {
  return apiCall<T>(url, undefined, { params, ...options })
}

export function post<T>(
  url: string,
  data: unknown = {},
  options?: CommonApiCallOptions
) {
  return apiCall<T>(url, data, { method: 'POST', ...options })
}

export function patch<T>(
  url: string,
  data: unknown = {},
  options?: CommonApiCallOptions
) {
  return apiCall<T>(url, data, { method: 'PATCH', ...options })
}

export function put<T>(
  url: string,
  data: unknown = {},
  options?: CommonApiCallOptions
) {
  return apiCall<T>(url, data, { method: 'PUT', ...options })
}

export function deleteReq<T>(
  url: string,
  data: unknown = {},
  options?: CommonApiCallOptions
) {
  return apiCall<T>(url, data, { method: 'DELETE', ...options })
}
